import { Avatar, CardActionArea, Chip, IconButton, Rating, Stack, Typography, useTheme } from '@mui/material';
import { Bolt, Link, LinkOff, Star } from '@mui/icons-material';
import { FC } from 'react';
import { graphql } from '../gql';
import { useApiMutation, useApiQuery } from '../query/hooks';
import { mutationResponseHandler } from '../utils/mutationResponseHandler';
import { useNavigate } from 'react-router';
import { useIntl } from 'react-intl';
import { useOpportunityStatusOptions } from '../opportunities/inputs/useOpportunityStatusOptions';
import { getNightOrDayPalette } from '../utils/getNightOrDayPalette';
import { StatusItem } from '../opportunities/inputs/StatusSelect';
import { useSnackbar } from '../store/snackbar';

const actionOpportunitiesList_getAction = graphql(`
	query actionOpportunitiesList_getAction($actionId: UUID!) {
		getAction(by: { id: $actionId }) {
			opportunities {
				id
				name
				status
				benefit
				owner {
					name
					avatar {
						url(width: 24, height: 24)
					}
				}
			}
		}
	}
`);

const actionOpportunitiesList_removeActionOpportunity = graphql(`
	mutation actionOpportunitiesList_removeActionOpportunity($actionId: UUID!, $opportunityId: UUID!) {
		updateAction(by: { id: $actionId }, data: { opportunities: { disconnect: { id: $opportunityId } } }) {
			ok
		}
	}
`);

const defaultBenefit = 5;

type OpportunitiesListProps = Readonly<{
	actionId: string;
	size: 'medium' | 'small';
}>;

export const OpportunitiesList: FC<OpportunitiesListProps> = ({ actionId, size }) => {
	const intl = useIntl();
	const theme = useTheme();
	const navigate = useNavigate();
	const { statusOptions } = useOpportunityStatusOptions();
	const { openSnackbar } = useSnackbar();

	const { data: action } = useApiQuery(actionOpportunitiesList_getAction, {
		variables: { actionId },
	});
	const { mutate: removeOpportunity } = useApiMutation(actionOpportunitiesList_removeActionOpportunity);

	const openOpportunity = (opportunityId: string) => {
		navigate(`/opportunities/${opportunityId}/detail`);
	};

	const handleRemoveOpportunity = (opportunityId: string) => {
		removeOpportunity(
			{ actionId, opportunityId },
			{
				onSuccess: (result) =>
					mutationResponseHandler({
						result: result.updateAction.ok,
						toastMessages: {
							success: intl.formatMessage({ id: 'action.remove-opportunity.success' }),
							error: intl.formatMessage({ id: 'action.remove-opportunity.error' }),
						},
						openSnackbar,
					}),
			},
		);
	};

	return (
		(action?.getAction?.opportunities.length ?? 0) > 0 && (
			<Stack direction={'column'} gap={1.5}>
				{action?.getAction?.opportunities.map((opportunity) => {
					const status = statusOptions.find((p) => p.value === opportunity.status)!;

					return (
						<CardActionArea key={opportunity.id} onClick={() => openOpportunity(opportunity.id)} sx={{ borderRadius: '6px' }}>
							<Stack
								direction={'row'}
								justifyContent={'space-between'}
								alignItems={'center'}
								gap={size === 'medium' ? 2.5 : 1.5}
								sx={{
									border: `1px solid ${theme.palette.grey[300]}`,
									borderRadius: '6px',
									padding: size === 'medium' ? '14px 20px 14px 20px' : '10px 8px 10px 12px',
								}}
							>
								<Stack flexBasis={0} flexGrow={1} direction={'row'} gap={size === 'medium' ? 1.5 : 1} alignItems={'center'}>
									<Bolt sx={{ fontSize: '20px', color: theme.palette.secondary.main }} />
									<Typography
										variant={'h6'}
										fontWeight={500}
										sx={{
											display: '-webkit-box',
											overflow: 'hidden',
											WebkitBoxOrient: 'vertical',
											WebkitLineClamp: 2,
										}}
									>
										{opportunity.name}
									</Typography>
								</Stack>
								{size === 'medium' && (
									<Rating
										readOnly
										value={opportunity.benefit || defaultBenefit}
										precision={1}
										size={'small'}
										emptyIcon={<Star style={{ color: theme.palette.secondary.light, fontSize: '18px' }} />}
									/>
								)}
								<Stack
									flexBasis={0}
									flexGrow={1}
									direction={'row'}
									gap={size === 'medium' ? 2.5 : 1.5}
									justifyContent={'flex-end'}
									alignItems={'center'}
								>
									{size === 'medium' ? (
										<>
											<StatusItem {...status} />
										</>
									) : (
										<Chip
											label={status.text}
											size={'small'}
											sx={{ color: theme.palette[getNightOrDayPalette(theme)][0], backgroundColor: status.color }}
										/>
									)}
									{size === 'medium' ? (
										<Stack
											justifyContent={'center'}
											alignItems={'center'}
											sx={{ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: '50%', minWidth: '32px', minHeight: '32px' }}
										>
											<Avatar
												alt={opportunity.owner?.name}
												src={opportunity.owner?.avatar?.url ?? undefined}
												sx={{ width: '24px', height: '24px' }}
											/>
										</Stack>
									) : (
										<Avatar
											alt={opportunity.owner?.name}
											src={opportunity.owner?.avatar?.url ?? undefined}
											sx={{ width: '24px', height: '24px' }}
										/>
									)}
									<IconButton
										onClick={(e) => {
											e.stopPropagation();
											handleRemoveOpportunity(opportunity.id);
										}}
										sx={{
											'&:not(:hover) > :nth-child(2)': { display: 'none' },
											'&:hover > :first-of-type': { display: 'none' },
											padding: '6px',
										}}
									>
										<Link sx={{ fill: theme.palette.secondary.main, fontSize: '20px' }} />
										<LinkOff sx={{ fill: theme.palette.secondary.main, fontSize: '20px' }} />
									</IconButton>
								</Stack>
							</Stack>
						</CardActionArea>
					);
				})}
			</Stack>
		)
	);
};
